import React, { useState, useRef, useEffect } from 'react';
import { claudeService } from '../services/claudeService';
import { useAuth } from '../context/AuthContext';
import { useProperty } from '../context/PropertyContext';
import { useSubscription } from '../context/SubscriptionContext';
import '../styles/ChatWidget.css';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const { user } = useAuth();
  const { getPropertyContext } = useProperty();
  const { canPerformAction, trackChatMessage } = useSubscription();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    // Check if user is authenticated
    if (!user) {
      const loginPromptMessage = {
        id: Date.now(),
        text: "Please log in to use the chat feature. You can sign up or log in using the menu above.",
        sender: 'assistant',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, loginPromptMessage]);
      setInputValue('');
      return;
    }

    // Note: Removed frontend-only limit check
    // Backend will now handle all limit validation

    const userMessage = inputValue.trim();
    setInputValue('');

    // Add user message to chat
    const newUserMessage = {
      id: Date.now(),
      text: userMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      // Prepare conversation history for Claude
      const conversationHistory = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));

      // Send message to Claude with property context
      const propertyContext = getPropertyContext();
      const response = await claudeService.sendMessageWithHistory(
        userMessage,
        conversationHistory,
        propertyContext,
        user?.userId
      );

      // Check if response indicates an error (like limit reached)
      if (!response.success) {
        throw new Error(response.error || response.message);
      }

      // Add Claude's response to chat
      const claudeMessage = {
        id: Date.now() + 1,
        text: response.message,
        sender: 'assistant',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, claudeMessage]);
      
      // Track usage after successful chat message
      if (user) {
        trackChatMessage();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Use the actual error message from the service
      const errorMessage = {
        id: Date.now() + 1,
        text: error.message || 'Sorry, I encountered an error. Please try again.',
        sender: 'assistant',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    
    // Add welcome message when opening for the first time
    if (!isOpen && messages.length === 0) {
      const propertyContext = getPropertyContext();
      const welcomeMessage = {
        id: Date.now(),
        text: `Hi! I'm your Property ROI assistant. I can help you understand real estate investments, analyze property data, and answer questions about ROI calculations. ${propertyContext ? 'I can see you have property data loaded - feel free to ask me about it!' : 'How can I help you today?'}`,
        sender: 'assistant',
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  };

  return (
    <div className="chat-widget">
      {/* Chat Toggle Button */}
      <button
        className={`chat-toggle ${isOpen ? 'open' : ''}`}
        onClick={toggleChat}
        aria-label="Toggle chat"
      >
        {isOpen ? '✕' : '💬'}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <h3>Property ROI Assistant</h3>
            <button
              className="chat-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              ✕
            </button>
          </div>
          

          <div className="chat-messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message ${message.sender === 'user' ? 'user-message' : 'assistant-message'}`}
              >
                <div className="message-content">
                  {message.text}
                </div>
                <div className="message-timestamp">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="message assistant-message">
                <div className="message-content typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me about property ROI, investments, or real estate..."
              disabled={isLoading}
              rows={1}
              className="chat-input"
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="send-button"
              aria-label="Send message"
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;